import React, {useEffect, useRef, useState} from "react";
import {api} from "../../api/client";
import {getTermRead} from "../../api/termRead";

type Closure = {id: number; title: string; reason: string; ends_at: string; ends_local: string; cancelled_at: string | null};
type Data = {issues?: Array<{id:number; title:string; problem:string}>; enabled: boolean; items: Closure[]; services: Array<{id: number; title: string}>};
const messages: Record<string, string> = {
    CLOSURE_REASON_REQUIRED: "Укажите причину закрытия — от 10 до 2000 символов.",
    CLOSURE_END_REQUIRED: "Укажите дату и время открытия услуги в будущем по времени салона.",
    CLOSURE_SERVICE_INVALID: "Выберите услугу действующего прайса этого салона.",
    CLOSURE_REQUEST_CONFLICT: "Детали запроса изменились. Обновите список и повторите действие.",
};

export function ServiceClosures({companyId}: {companyId: string}) {
    const [data, setData] = useState<Data | null>(null);
    const [serviceId, setServiceId] = useState("");
    const [reason, setReason] = useState("");
    const [until, setUntil] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const mounted = useRef(true);
    const previous = useRef<{fingerprint: string; id: string} | null>(null);
    useEffect(() => {
        mounted.current = true;
        const controller = new AbortController();
        getTermRead<Data>("/lkfr/records/service-closures", {}, controller.signal)
            .then(result => {if (!controller.signal.aborted) setData(result);})
            .catch(() => {if (!controller.signal.aborted) setError("Не удалось загрузить временные закрытия услуг.");});
        return () => {mounted.current = false; controller.abort();};
    }, [companyId]);

    const mutate = async (reopenId?: number) => {
        if (busy) return;
        setBusy(true); setError("");
        try {
            if (reopenId) {
                await api.post(`/api/gateway/records/service-closures/${reopenId}/reopen`, {companyIdApi: companyId});
                const result = await getTermRead<Data>("/lkfr/records/service-closures");
                if (mounted.current) setData(result);
            } else {
                const fingerprint = JSON.stringify([serviceId, reason, until]);
                if (previous.current?.fingerprint !== fingerprint) previous.current = {
                    fingerprint,
                    id: typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                        ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
                };
                const result = await api.post<Data>("/api/gateway/records/service-closures", {
                    companyIdApi: companyId, serviceId: Number(serviceId), reason, until, requestId: previous.current.id,
                });
                if (mounted.current) {setData(result); setReason(""); setUntil(""); previous.current = null;}
            }
        } catch (e: any) {
            if (mounted.current) setError(messages[e?.details?.error || e?.response?.data?.error] || "Не удалось сохранить изменение. Повторите запрос.");
        } finally {if (mounted.current) setBusy(false);}
    };

    if (!data?.enabled) return error ? <div className="section-card section-helper">{error}</div> : null;
    return <details className="section-card records-service-closures">
        <summary className="section-subtitle">Временное закрытие услуги</summary>
        <p>Если для базовой услуги не настроены мастера или график, клиент может записаться в салон. Мастера нужно назначить после записи. Здесь можно временно закрыть такую услугу с причиной и сроком.</p>
        {data.issues?.map(issue => <p key={issue.id} role="status"><strong>{issue.title}:</strong> {issue.problem}. Заполните действующий прайс.</p>)}
        <form onSubmit={event => {event.preventDefault(); void mutate();}}>
            <label>Услуга<select className="section-input" required value={serviceId} onChange={e => setServiceId(e.target.value)} disabled={busy}>
                <option value="">Выберите услугу</option>{data.services.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select></label>
            <label>Причина<textarea className="section-input" required minLength={10} maxLength={2000} value={reason} onChange={e => setReason(e.target.value)} disabled={busy}/></label>
            <label>Открыть услугу — по времени салона<input className="section-input" type="datetime-local" required value={until} onChange={e => setUntil(e.target.value)} disabled={busy}/></label>
            <p>В указанный срок запись на услугу откроется автоматически. Уже принятые записи требуют исполнения.</p>
            <button className="section-btn" disabled={busy} type="submit">{busy ? "Сохраняем…" : "Временно закрыть и уведомить УК"}</button>
        </form>
        {error ? <p role="alert">{error}</p> : null}
        {data.items.map(item => <div key={item.id}>
            <strong>{item.title}</strong><p>{item.reason}</p>
            <p>{!item.cancelled_at && new Date(item.ends_at).getTime() > Date.now() ? `Закрыта до ${item.ends_local} по времени салона` : "Запись открыта"}</p>
            {!item.cancelled_at && new Date(item.ends_at).getTime() > Date.now() ? <button className="section-btn" type="button" disabled={busy} onClick={() => void mutate(item.id)}>Открыть сейчас</button> : null}
        </div>)}
    </details>;
}
